/**
 * HARMONY — live inter-module event observatory.
 *
 * Three views, one page:
 *   • Live stream — last N typed events from /api/harmony/events (polls every 5s).
 *   • Coverage   — emit/subscribe matrix from /api/harmony/coverage.
 *   • Workflows  — curated cross-module flows from /api/harmony/workflows.
 *
 * Read-only: no mutations, no second EventSource, no api.ts touch.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BRAND } from "../../branding";

// ── Types ─────────────────────────────────────────────────────────────────────

interface HarmonyEvent {
  event_id: string;
  event_type: string;
  source_module: string;
  timestamp: string;
  priority: "low" | "normal" | "high" | "critical" | string;
  correlation_id: string | null;
  payload: Record<string, any>;
}

interface CoverageEvent {
  event_type: string;
  source_module: string;
  subscribers: string[];
  subscriber_count: number;
  has_handler: boolean;
}

interface CoverageRow {
  module: string;
  cells: Record<string, "emit" | "subscribe" | "both">;
}

interface CoverageResponse {
  modules: string[];
  event_types: string[];
  events: CoverageEvent[];
  matrix: CoverageRow[];
  totals: { modules: number; event_types: number; registered: number; orphans: number };
}

interface WorkflowStep {
  from: string;
  event_type: string;
  to: string;
  label: string;
}

interface Workflow {
  id: string;
  title: string;
  description: string;
  steps: WorkflowStep[];
}

type TabKey = "live" | "coverage" | "workflows";

// ── Constants & helpers ───────────────────────────────────────────────────────

const POLL_MS = 5000;
const EVENT_LIMIT = 50;

const PRIORITY_COLOR: Record<string, string> = {
  critical: BRAND.colors.danger,
  high:     BRAND.colors.warning,
  normal:   BRAND.colors.primary,
  low:      BRAND.colors.textMuted,
};

const ROLE_GLYPH: Record<"emit" | "subscribe" | "both", string> = {
  emit: "↑",
  subscribe: "↓",
  both: "↕",
};
const ROLE_COLOR: Record<"emit" | "subscribe" | "both", string> = {
  emit: BRAND.colors.warning,
  subscribe: BRAND.colors.primary,
  both: BRAND.colors.accent,
};
const ROLE_LABEL: Record<"emit" | "subscribe" | "both", string> = {
  emit: "Emits",
  subscribe: "Subscribes",
  both: "Both",
};

const glassStyle: React.CSSProperties = {
  background: BRAND.glassEffect,
  border: "1px solid rgba(189,178,255,0.12)",
  borderRadius: BRAND.borderRadius,
  padding: "16px 20px",
  backdropFilter: BRAND.blur,
  WebkitBackdropFilter: BRAND.blur,
};

const formatTime = (iso: string, recent: boolean): string => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return recent
      ? d.toLocaleTimeString("en-GB", { hour12: false })
      : d.toLocaleString("en-GB", { hour12: false });
  } catch {
    return iso;
  }
};

const fetchJSON = async <T,>(url: string): Promise<T> => {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return (await res.json()) as T;
};

// ── Small UI atoms ────────────────────────────────────────────────────────────

const Badge: React.FC<{ children: React.ReactNode; color: string; title?: string }> = ({
  children, color, title,
}) => (
  <span title={title} style={{
    background: `${color}1f`,
    border: `1px solid ${color}55`,
    color,
    borderRadius: 6,
    padding: "1px 8px",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.6,
    fontFamily: "'Space Grotesk', sans-serif",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  }}>{children}</span>
);

const StatCard: React.FC<{ label: string; value: number | string; sub?: string }> = ({
  label, value, sub,
}) => (
  <div style={glassStyle}>
    <div style={{ fontSize: 11, color: BRAND.colors.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 26, fontWeight: 800, color: BRAND.colors.primary, fontFamily: "'Manrope', sans-serif" }}>{value}</div>
    {sub && <div style={{ fontSize: 10, color: BRAND.colors.textMuted, marginTop: 2 }}>{sub}</div>}
  </div>
);

// ── Live stream tab ───────────────────────────────────────────────────────────

const LiveTab: React.FC<{
  events: HarmonyEvent[];
  connected: boolean;
  bufferSize: number;
}> = ({ events, connected, bufferSize }) => {
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");

  const eventTypes = useMemo(
    () => Array.from(new Set(events.map(e => e.event_type).filter(Boolean))).sort(),
    [events],
  );
  const sourceModules = useMemo(
    () => Array.from(new Set(events.map(e => e.source_module).filter(Boolean))).sort(),
    [events],
  );

  const filtered = useMemo(
    () => events.filter(e =>
      (!eventTypeFilter || e.event_type === eventTypeFilter) &&
      (!moduleFilter || e.source_module === moduleFilter)
    ),
    [events, eventTypeFilter, moduleFilter],
  );

  const selectStyle: React.CSSProperties = {
    background: "rgba(6,14,32,0.6)",
    border: "1px solid rgba(189,178,255,0.18)",
    color: "#dde5ff",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 12,
    fontFamily: BRAND.fontFamily,
    minWidth: 180,
  };

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12, marginBottom: 20 }}>
        <StatCard label="Events buffered" value={events.length} sub={`server buffer: ${bufferSize}`} />
        <StatCard label="Distinct event types" value={eventTypes.length} sub="in current view" />
        <StatCard label="Distinct source modules" value={sourceModules.length} sub="in current view" />
        <StatCard
          label="Bus connection"
          value={connected ? "LIVE" : "STALLED"}
          sub={connected ? `polling every ${POLL_MS / 1000}s` : "last poll failed"}
        />
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: BRAND.colors.textMuted, textTransform: "uppercase", letterSpacing: 1 }}>Filter</span>
        <select value={eventTypeFilter} onChange={e => setEventTypeFilter(e.target.value)} style={selectStyle}>
          <option value="">All event types</option>
          {eventTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} style={selectStyle}>
          <option value="">All source modules</option>
          {sourceModules.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        {(eventTypeFilter || moduleFilter) && (
          <button onClick={() => { setEventTypeFilter(""); setModuleFilter(""); }} style={{
            background: "transparent", border: `1px solid ${BRAND.colors.textMuted}55`, color: BRAND.colors.textMuted,
            borderRadius: 8, padding: "5px 10px", fontSize: 11, cursor: "pointer", fontFamily: BRAND.fontFamily,
          }}>Clear</button>
        )}
        <span style={{ marginLeft: "auto", fontSize: 11, color: BRAND.colors.textMuted }}>
          Showing {filtered.length} of {events.length}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div style={{ ...glassStyle, padding: "36px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 14, color: BRAND.colors.textMuted, marginBottom: 12 }}>
            {events.length === 0
              ? "No typed events yet. Trigger a workflow on the bus and it will surface here within 5s."
              : "No events match the current filters."}
          </div>
          {events.length === 0 && (
            <div style={{ fontSize: 11, color: BRAND.colors.textMuted, lineHeight: 1.6 }}>
              Try logging in, classifying an inbound email, or approving an AI workflow.
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map((e, idx) => {
            const priorityColor = PRIORITY_COLOR[e.priority] || BRAND.colors.primary;
            return (
              <div key={e.event_id} style={{
                ...glassStyle, padding: "12px 16px",
                borderLeft: `2px solid ${priorityColor}`,
                animation: idx === 0 ? "harmony-fade-in 250ms ease-out" : undefined,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, color: BRAND.colors.textMuted, fontFamily: "'Space Grotesk', monospace", minWidth: 88 }}>
                    {formatTime(e.timestamp, idx < 10)}
                  </span>
                  <Badge color={BRAND.colors.primary}>{e.event_type || "—"}</Badge>
                  <Badge color={BRAND.colors.textMuted}>{e.source_module || "—"}</Badge>
                  {e.priority && e.priority !== "normal" && (
                    <Badge color={priorityColor}>{e.priority}</Badge>
                  )}
                  {e.correlation_id && (
                    <span style={{ fontSize: 10, color: BRAND.colors.textMuted, fontFamily: "monospace" }} title="correlation_id">
                      ⟶ {e.correlation_id.slice(0, 8)}
                    </span>
                  )}
                </div>
                {Object.keys(e.payload || {}).length > 0 && (
                  <details style={{ marginTop: 4 }}>
                    <summary style={{
                      fontSize: 11, color: BRAND.colors.textMuted, cursor: "pointer",
                      fontFamily: "'Space Grotesk', sans-serif",
                    }}>payload</summary>
                    <pre style={{
                      margin: "6px 0 0 0", fontSize: 11, color: BRAND.colors.textMuted,
                      background: "rgba(6,14,32,0.5)", padding: 10, borderRadius: 8,
                      maxHeight: 220, overflow: "auto",
                      border: "1px solid rgba(189,178,255,0.08)",
                    }}>{JSON.stringify(e.payload, null, 2)}</pre>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

// ── Coverage tab ──────────────────────────────────────────────────────────────

const CoverageTab: React.FC<{ data: CoverageResponse | null; loading: boolean; error: string | null }> = ({
  data, loading, error,
}) => {
  if (loading && !data) {
    return <div style={{ ...glassStyle, textAlign: "center" }}>Loading coverage matrix…</div>;
  }
  if (error) {
    return (
      <div style={{ ...glassStyle, borderColor: `${BRAND.colors.danger}55`, color: BRAND.colors.danger }}>
        Failed to load coverage: {error}
      </div>
    );
  }
  if (!data) return null;

  const { modules, event_types, matrix, events, totals } = data;

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12, marginBottom: 20 }}>
        <StatCard label="Modules tracked" value={totals.modules} sub="emit and/or subscribe" />
        <StatCard label="Event types registered" value={totals.event_types} sub="in EVENT_REGISTRY" />
        <StatCard label="Wired" value={totals.registered} sub="have ≥1 subscriber" />
        <StatCard label="Orphans" value={totals.orphans} sub="no live handler" />
      </div>

      <div style={{ ...glassStyle, padding: 0, marginBottom: 20, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{
            borderCollapse: "collapse", width: "100%",
            fontFamily: "'Space Grotesk', sans-serif", fontSize: 11,
          }}>
            <thead>
              <tr style={{ background: "rgba(189,178,255,0.06)" }}>
                <th style={{
                  position: "sticky", left: 0, zIndex: 2,
                  background: "rgba(15,21,42,0.95)",
                  padding: "10px 14px", textAlign: "left",
                  fontSize: 10, color: BRAND.colors.textMuted, letterSpacing: 1,
                  textTransform: "uppercase", borderBottom: "1px solid rgba(189,178,255,0.12)",
                }}>Module \ Event</th>
                {event_types.map(et => (
                  <th key={et} style={{
                    padding: "10px 8px", whiteSpace: "nowrap",
                    fontSize: 10, color: BRAND.colors.primary, fontWeight: 700,
                    borderBottom: "1px solid rgba(189,178,255,0.12)",
                    textAlign: "center",
                  }}>
                    <div style={{
                      writingMode: "vertical-rl", transform: "rotate(180deg)",
                      maxHeight: 140, padding: "4px 0",
                    }}>{et}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.map(row => (
                <tr key={row.module}>
                  <td style={{
                    position: "sticky", left: 0, zIndex: 1,
                    background: "rgba(15,21,42,0.95)",
                    padding: "8px 14px", color: "#dde5ff", fontWeight: 600,
                    borderBottom: "1px solid rgba(189,178,255,0.06)",
                    whiteSpace: "nowrap",
                  }}>{row.module}</td>
                  {event_types.map(et => {
                    const role = row.cells[et];
                    if (!role) {
                      return (
                        <td key={et} style={{
                          textAlign: "center", padding: "8px 4px",
                          color: "rgba(189,178,255,0.10)",
                          borderBottom: "1px solid rgba(189,178,255,0.04)",
                        }}>·</td>
                      );
                    }
                    const color = ROLE_COLOR[role];
                    return (
                      <td
                        key={et}
                        title={`${row.module} ${ROLE_LABEL[role]} ${et}`}
                        style={{
                          textAlign: "center", padding: "6px 4px",
                          background: `${color}14`,
                          borderBottom: "1px solid rgba(189,178,255,0.04)",
                        }}
                      >
                        <span style={{
                          display: "inline-block", minWidth: 18,
                          color, fontWeight: 800, fontSize: 13,
                        }}>{ROLE_GLYPH[role]}</span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{
          padding: "8px 14px", borderTop: "1px solid rgba(189,178,255,0.08)",
          display: "flex", gap: 18, fontSize: 10, color: BRAND.colors.textMuted,
        }}>
          <span><span style={{ color: ROLE_COLOR.emit, fontWeight: 800 }}>↑</span> emits</span>
          <span><span style={{ color: ROLE_COLOR.subscribe, fontWeight: 800 }}>↓</span> subscribes</span>
          <span><span style={{ color: ROLE_COLOR.both, fontWeight: 800 }}>↕</span> both</span>
        </div>
      </div>

      <h3 style={{
        margin: "0 0 10px 0", fontSize: 13,
        color: BRAND.colors.textMuted, textTransform: "uppercase", letterSpacing: 1.2,
        fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
      }}>Event registry</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
        {events.map(e => (
          <div key={e.event_type} style={{
            ...glassStyle, padding: "12px 14px",
            borderLeft: `2px solid ${e.has_handler ? BRAND.colors.accent : BRAND.colors.danger}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
              <Badge color={BRAND.colors.primary}>{e.event_type}</Badge>
              {!e.has_handler && <Badge color={BRAND.colors.danger}>orphan</Badge>}
            </div>
            <div style={{ fontSize: 11, color: BRAND.colors.textMuted, lineHeight: 1.6 }}>
              <div><span style={{ color: ROLE_COLOR.emit }}>emits:</span> {e.source_module || "—"}</div>
              <div><span style={{ color: ROLE_COLOR.subscribe }}>subscribers:</span>{" "}
                {e.subscribers.length === 0 ? "—" : e.subscribers.join(", ")}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

// ── Workflows tab ─────────────────────────────────────────────────────────────

const WorkflowsTab: React.FC<{
  workflows: Workflow[];
  coverage: CoverageResponse | null;
  loading: boolean;
  error: string | null;
}> = ({ workflows, coverage, loading, error }) => {
  if (loading && workflows.length === 0) {
    return <div style={{ ...glassStyle, textAlign: "center" }}>Loading workflows…</div>;
  }
  if (error) {
    return (
      <div style={{ ...glassStyle, borderColor: `${BRAND.colors.danger}55`, color: BRAND.colors.danger }}>
        Failed to load workflows: {error}
      </div>
    );
  }

  // Map event_type -> coverage info for status decoration.
  const coverageByEvent = useMemo(() => {
    const map: Record<string, CoverageEvent> = {};
    coverage?.events.forEach(e => { map[e.event_type] = e; });
    return map;
  }, [coverage]);

  const Pill: React.FC<{ label: string; tone?: "module" | "event" | "muted" }> = ({ label, tone = "module" }) => {
    const color = tone === "event" ? BRAND.colors.primary
                : tone === "muted" ? BRAND.colors.textMuted
                : BRAND.colors.accent;
    return (
      <span style={{
        background: `${color}14`,
        border: `1px solid ${color}40`,
        color,
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: "'Space Grotesk', sans-serif",
        whiteSpace: "nowrap",
      }}>{label}</span>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {workflows.map(wf => (
        <div key={wf.id} style={glassStyle}>
          <div style={{ marginBottom: 8 }}>
            <h3 style={{
              margin: "0 0 4px 0", fontSize: 16, fontWeight: 800,
              color: "#dde5ff", fontFamily: "'Manrope', sans-serif",
            }}>{wf.title}</h3>
            <p style={{ margin: 0, fontSize: 12, color: BRAND.colors.textMuted, lineHeight: 1.6 }}>
              {wf.description}
            </p>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginTop: 14 }}>
            {wf.steps.map((step, idx) => {
              const cov = coverageByEvent[step.event_type];
              const wired = cov?.has_handler ?? false;
              return (
                <React.Fragment key={`${wf.id}-${idx}`}>
                  <Pill label={step.from} tone="module" />
                  <span aria-hidden style={{ color: BRAND.colors.textMuted, fontSize: 16, padding: "0 2px" }}>→</span>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                    <Pill label={step.event_type} tone="event" />
                    <span style={{
                      fontSize: 9, color: wired ? BRAND.colors.accent : BRAND.colors.danger,
                      letterSpacing: 0.8, textTransform: "uppercase", fontWeight: 700,
                      fontFamily: "'Space Grotesk', sans-serif",
                    }}>
                      {wired ? "● wired" : "○ orphan"}
                    </span>
                  </div>
                  <span aria-hidden style={{ color: BRAND.colors.textMuted, fontSize: 16, padding: "0 2px" }}>→</span>
                  <Pill label={step.to} tone="module" />
                  {idx < wf.steps.length - 1 && (
                    <span style={{ flexBasis: "100%", height: 6 }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────────

const HarmonyDashboard: React.FC = () => {
  const [tab, setTab] = useState<TabKey>("live");

  // Live stream state
  const [events, setEvents] = useState<HarmonyEvent[]>([]);
  const [bufferSize, setBufferSize] = useState(0);
  const [connected, setConnected] = useState(false);
  const lastTsRef = useRef<string | null>(null);

  // Coverage + workflows
  const [coverage, setCoverage] = useState<CoverageResponse | null>(null);
  const [coverageLoading, setCoverageLoading] = useState(true);
  const [coverageError, setCoverageError] = useState<string | null>(null);

  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(true);
  const [workflowsError, setWorkflowsError] = useState<string | null>(null);

  // ── Live stream poller ──────────────────────────────────────────
  const pollEvents = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: String(EVENT_LIMIT) });
      if (lastTsRef.current) params.set("since", lastTsRef.current);
      const data = await fetchJSON<{
        events: HarmonyEvent[]; count: number; buffer_size: number; server_time: string;
      }>(`/api/harmony/events?${params.toString()}`);
      setBufferSize(data.buffer_size);
      setConnected(true);
      if (data.events.length > 0) {
        setEvents(prev => {
          const merged = [...data.events, ...prev];
          // Dedupe by event_id; keep newest-first ordering.
          const seen = new Set<string>();
          const out: HarmonyEvent[] = [];
          for (const e of merged) {
            if (!seen.has(e.event_id)) {
              seen.add(e.event_id);
              out.push(e);
            }
          }
          return out.slice(0, EVENT_LIMIT);
        });
        // Cursor advances to the newest event's timestamp.
        lastTsRef.current = data.events[0].timestamp;
      } else if (!lastTsRef.current && data.server_time) {
        // No events yet — anchor cursor so subsequent polls only show new ones.
        lastTsRef.current = data.server_time;
      }
    } catch (err: any) {
      setConnected(false);
    }
  }, []);

  useEffect(() => {
    pollEvents();
    const id = setInterval(pollEvents, POLL_MS);
    return () => clearInterval(id);
  }, [pollEvents]);

  // ── One-shot loaders for coverage + workflows ───────────────────
  const loadCoverage = useCallback(async () => {
    setCoverageLoading(true);
    setCoverageError(null);
    try {
      const data = await fetchJSON<CoverageResponse>("/api/harmony/coverage");
      setCoverage(data);
    } catch (err: any) {
      setCoverageError(err?.message || "request failed");
    } finally {
      setCoverageLoading(false);
    }
  }, []);

  const loadWorkflows = useCallback(async () => {
    setWorkflowsLoading(true);
    setWorkflowsError(null);
    try {
      const data = await fetchJSON<{ workflows: Workflow[]; count: number }>("/api/harmony/workflows");
      setWorkflows(data.workflows);
    } catch (err: any) {
      setWorkflowsError(err?.message || "request failed");
    } finally {
      setWorkflowsLoading(false);
    }
  }, []);

  useEffect(() => { loadCoverage(); loadWorkflows(); }, [loadCoverage, loadWorkflows]);

  // ── Render ──────────────────────────────────────────────────────
  const dotColor = connected ? BRAND.colors.accent : BRAND.colors.danger;

  const tabs: { key: TabKey; label: string; count?: number }[] = [
    { key: "live",      label: "Live stream",   count: events.length },
    { key: "coverage",  label: "Coverage",      count: coverage?.totals.event_types },
    { key: "workflows", label: "Workflows",     count: workflows.length },
  ];

  return (
    <div style={{
      padding: "24px 28px", fontFamily: BRAND.fontFamily,
      color: "#dde5ff", background: BRAND.colors.secondary, minHeight: "100%",
    }}>
      <style>{`
        @keyframes harmony-pulse  { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
        @keyframes harmony-fade-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
      `}</style>

      <div style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 26 }}>🜁</span>
          <h1 style={{
            margin: 0, fontSize: 24, fontWeight: 800,
            fontFamily: "'Manrope', sans-serif",
            background: `linear-gradient(90deg, ${BRAND.colors.primary}, ${BRAND.colors.accent})`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            letterSpacing: 1.5,
          }}>HARMONY</h1>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: `${dotColor}1a`, border: `1px solid ${dotColor}40`,
            color: dotColor, borderRadius: 6, padding: "2px 10px",
            fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
            fontFamily: "'Space Grotesk', sans-serif",
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: "50%", background: dotColor,
              animation: connected ? "harmony-pulse 2s ease-in-out infinite" : "none",
            }} />
            {connected ? "LIVE" : "STALLED"}
          </span>
        </div>
        <p style={{ margin: 0, color: BRAND.colors.textMuted, fontSize: 13 }}>
          Live inter-module event flow · typed bus · polls{" "}
          <code style={{ color: BRAND.colors.primary }}>/api/harmony/events</code> every {POLL_MS / 1000}s
        </p>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "1px solid rgba(189,178,255,0.10)" }}>
        {tabs.map(t => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                background: "transparent",
                border: "none",
                color: active ? BRAND.colors.primary : BRAND.colors.textMuted,
                fontFamily: BRAND.fontFamily,
                fontSize: 13, fontWeight: 700, letterSpacing: 0.4,
                padding: "10px 18px", cursor: "pointer",
                borderBottom: `2px solid ${active ? BRAND.colors.primary : "transparent"}`,
                marginBottom: -1,
              }}
            >
              {t.label}
              {typeof t.count === "number" && (
                <span style={{
                  marginLeft: 8, fontSize: 10, fontWeight: 700,
                  color: BRAND.colors.textMuted, fontFamily: "'Space Grotesk', sans-serif",
                }}>{t.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "live" && (
        <LiveTab events={events} connected={connected} bufferSize={bufferSize} />
      )}
      {tab === "coverage" && (
        <CoverageTab data={coverage} loading={coverageLoading} error={coverageError} />
      )}
      {tab === "workflows" && (
        <WorkflowsTab
          workflows={workflows}
          coverage={coverage}
          loading={workflowsLoading || coverageLoading}
          error={workflowsError}
        />
      )}
    </div>
  );
};

export default HarmonyDashboard;
